import { createFileRoute, redirect } from "@tanstack/react-router";

import { userOrganizationsQueryOptions } from "@mountayaapp/api";

import { env } from "../env";
import { OrganizationsEmptyState } from "../iam/organization/components/OrganizationsEmptyState";
import { resolveCurrentOrganizationId } from "../iam/shared/rules/organizationResolution";
import { readRememberedOrganizationId } from "../iam/shared/rules/rememberedOrganization";

/**
 * Route is /organizations (index): it resolves the organization the user should
 * land in and redirects there, falling back to the empty state when the user has no
 * active membership. The resolution reuses the shared userOrganizations cache (the
 * same entry the _app guard prefetched, so no extra fetch) and the pure resolver —
 * remembered org first, then the first active by name. A lookup failure degrades to
 * the empty state rather than blanking the route.
 */
export const Route = createFileRoute("/_app/organizations/")({
  beforeLoad: async ({ context }) => {
    // The _app guard above has already refused a visitor without a session, so the
    // signed-in user is known here; without one there is no membership to land in.
    const userId = context.session?.userId;
    const memberships = userId
      ? await context.queryClient
          .ensureQueryData(userOrganizationsQueryOptions({ baseUrl: env.apiBaseUrl }, userId))
          .catch(() => [])
      : [];

    const target = resolveCurrentOrganizationId(memberships, {
      rememberedId: readRememberedOrganizationId(),
      routeId: null,
      storeId: context.identityStore.getState().organizationId,
    });

    if (target !== null) {
      throw redirect({
        params: { organizationId: target },
        to: "/organizations/$organizationId/members",
      });
    }
  },
  component: OrganizationsEmptyState,
});
